import React from "react";
import { DOC_CENTER } from "@/constants/testIds";
import {
  ArrowUpRight,
  Ship,
  Box,
  Building2,
  CalendarDays,
  FileSignature,
  ReceiptText,
  Package,
  Anchor,
  Award,
  ClipboardCheck,
  Truck,
  ShieldCheck,
  GitCommit,
  CircleCheck,
} from "lucide-react";

const DOCS = [
  { slug: "purchase-order", title: "Purchase Order", code: "PO-2481-A", version: "v3.2", uploader: "Aanya R.", updated: "2h ago", icon: ClipboardCheck, status: "approved" },
  { slug: "commercial-invoice", title: "Commercial Invoice", code: "CI-44821", version: "v2.1", uploader: "Mateus S.", updated: "5h ago", icon: ReceiptText, status: "approved" },
  { slug: "packing-list", title: "Packing List", code: "PL-44821", version: "v1.4", uploader: "Aanya R.", updated: "1d ago", icon: Package, status: "approved" },
  { slug: "bill-of-lading", title: "Bill of Lading", code: "BL-MSCU-2481", version: "v1.0", uploader: "Karim O.", updated: "3d ago", icon: Anchor, status: "approved" },
  { slug: "certificate-of-origin", title: "Certificate of Origin", code: "COO-IN-9912", version: "v1.1", uploader: "Supplier", updated: "3d ago", icon: Award, status: "approved" },
  { slug: "inspection-report", title: "Inspection Report", code: "INS-7740", version: "v1.0", uploader: "Inspection™️", updated: "1d ago", icon: FileSignature, status: "approved" },
  { slug: "freight-invoice", title: "Freight Invoice", code: "FRT-552201", version: "v1.0", uploader: "FreightIQ™️", updated: "12h ago", icon: Truck, status: "pending" },
  { slug: "insurance-certificate", title: "Insurance Certificate", code: "INSR-09812", version: "v1.0", uploader: "Compliance", updated: "1d ago", icon: ShieldCheck, status: "approved" },
];

function DocumentRecordCard({ doc, index }) {
  const Icon = doc.icon;
  const isApproved = doc.status === "approved";
  return (
    <div
      data-testid={DOC_CENTER.hero.documentCard(doc.slug)}
      className="dc-card dc-card-hover group relative p-4 dc-rise"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--dc-border)] bg-white text-ink-700 transition-colors group-hover:border-ink-300">
            <Icon className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div>
            <div className="text-[13.5px] font-semibold leading-tight text-ink-950">{doc.title}</div>
            <div className="mt-1 font-mono text-[10.5px] uppercase tracking-wider text-ink-500">{doc.code}</div>
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
            isApproved
              ? "bg-[color:var(--dc-approved-bg)] text-[color:var(--dc-approved-text)]"
              : "bg-[color:var(--dc-warn-bg)] text-[color:var(--dc-warn-text)]"
          }`}
        >
          <CircleCheck className="h-3 w-3" strokeWidth={2} />
          {isApproved ? "Approved" : "Pending"}
        </span>
      </div>

      <div className="mt-3.5 grid grid-cols-2 gap-2 border-t border-dashed border-[color:var(--dc-border)] pt-3 text-[11px]">
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-wider text-ink-400">Version</div>
          <div className="mt-0.5 flex items-center gap-1 font-mono text-ink-800">
            <GitCommit className="h-3 w-3" strokeWidth={1.75} /> {doc.version}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-wider text-ink-400">Uploaded by</div>
          <div className="mt-0.5 truncate text-ink-800">{doc.uploader}</div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-wider text-ink-400">Updated</div>
          <div className="mt-0.5 text-ink-800">{doc.updated}</div>
        </div>
        <div>
          <div className="font-mono text-[9.5px] uppercase tracking-wider text-ink-400">Linked</div>
          <div className="mt-0.5 font-mono text-ink-800">SH-2481</div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="hero" data-testid={DOC_CENTER.hero.section} className="relative overflow-hidden pt-10 md:pt-16">
      {/* grid background */}
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-[680px] dc-grid-bg mask-fade-bottom" aria-hidden />

      <div className="dc-container relative">
        {/* eyebrow + headline */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="dc-eyebrow dc-eyebrow-center">Workspace Product</span>
          <h1 className="mt-5 font-display text-[44px] font-black leading-[1.02] tracking-tighter text-ink-950 sm:text-6xl lg:text-[76px]">
            Document Center<span className="align-super text-[0.45em] font-bold">™</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl font-display text-2xl font-bold leading-[1.15] tracking-tight text-ink-950 sm:text-[28px]">
            Every Import Document.
            <br />
            <span className="text-ink-500">Exactly Where You Need It.</span>
          </p>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-ink-600">
            Stop searching through emails, folders and WhatsApp conversations. Access every document
            connected to your purchase orders, shipments and suppliers inside one Workspace.
          </p>

          <div className="mt-7 flex justify-center">
            <a href="#hero" data-testid={DOC_CENTER.hero.primaryCta} className="dc-btn-primary">
              Open Document Center™
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </a>
          </div>
        </div>

        {/* Workspace mock */}
        <div className="relative mt-12 md:mt-20" data-testid={DOC_CENTER.hero.workspaceMock}>
          <div className="overflow-hidden rounded-[20px] border border-[color:var(--dc-border)] bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)]">
            {/* Window chrome */}
            <div className="flex items-center justify-between border-b border-[color:var(--dc-border)] bg-white/70 px-5 py-3 dc-glass">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
                <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
                <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
                <span className="ml-3 font-mono text-[11px] uppercase tracking-wider text-ink-500">
                  workspace / shipments / SH-2481
                </span>
              </div>
              <div className="hidden items-center gap-2 sm:flex">
                <span className="dc-tag"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Synced live</span>
                <span className="dc-tag font-mono">8 documents</span>
              </div>
            </div>

            {/* Shipment context bar */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-[color:var(--dc-border)] bg-gradient-to-b from-white to-[#FAFAFA] px-5 py-5 sm:grid-cols-4 sm:px-7">
              <ContextField label="Shipment" value="SH-2481" mono icon={Ship} accent />
              <ContextField label="Supplier" value="ABC Foods" icon={Building2} />
              <ContextField label="Container" value="MSCU1234567" mono icon={Box} />
              <ContextField label="ETA" value="12 June" icon={CalendarDays} />
            </div>

            {/* Document grid */}
            <div className="grid grid-cols-1 gap-3 bg-[color:var(--dc-bg)] p-5 sm:grid-cols-2 sm:p-6 lg:grid-cols-4">
              {DOCS.map((d, i) => (
                <DocumentRecordCard doc={d} key={d.slug} index={i} />
              ))}
            </div>

            {/* Footer rail */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--dc-border)] bg-white px-5 py-3 text-[11.5px] text-ink-500 sm:px-7">
              <div className="flex items-center gap-3">
                <span className="font-mono uppercase tracking-wider">Connected to</span>
                <span className="dc-tag">RFQ™</span>
                <span className="dc-tag">CommodityBid™</span>
                <span className="dc-tag">FreightIQ™</span>
                <span className="dc-tag">Inspection™</span>
              </div>
              <span className="font-mono text-[10.5px]">Last activity 2 min ago · automation: ON</span>
            </div>
          </div>

          {/* floating badge */}
          <div className="absolute -right-2 -top-3 hidden rotate-2 md:block">
            <div className="dc-card flex items-center gap-2 px-3 py-2 shadow-md">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              <span className="font-mono text-[10.5px] uppercase tracking-wider text-ink-700">Operation linked · 8/8 documents</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContextField({ label, value, mono, icon: Icon, accent }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
        <Icon className="h-3 w-3" strokeWidth={1.75} />
        {label}
      </div>
      <div className={`mt-1 ${mono ? "font-mono" : "font-display font-extrabold"} text-[15px] tracking-tight ${accent ? "text-[color:var(--dc-accent)]" : "text-ink-950"}`}>
        {value}
      </div>
    </div>
  );
}
